/**
 * The floor-plan editor (10-web-admin § 6.3, § 6.6).
 *
 * The document lives in `editorStore`; every mutation goes through the pure
 * functions in `@cowoo/shared`. This file is wiring: keyboard, autosave, and
 * the conflict dialog that § 6.6 requires instead of a merge.
 *
 * Laid out as the board draws it — `56px 232px 1fr 320px`: an icon rail, the
 * object list, the canvas, the property panel — because the widths are part
 * of the design, not a detail of it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card } from '@cowoo/shared/ui-web';
import {
  clampToBounds,
  DEFAULT_RULES,
  DEFAULT_SIZE_CM,
  duplicateObject,
  formatTime,
  moveObject,
  nextFreeLabel,
  rotateObject,
  type EditorObject,
  clampSize,
  snapToGrid,
} from '@cowoo/shared';
import { api } from '../../lib/api';
import { DraftChip, EditorBar, Icon, Kbd, Switch } from '../../ui';
import { Canvas } from './Canvas';
import { ObjectList, ToolRail, toolForKey, type Tool } from './ToolRail';
import { ObjectPanel } from './ObjectPanel';
import {
  AUTOSAVE_DEBOUNCE_MS,
  backoffFor,
  shouldWarnOnUnload,
  useEditorStore,
  type EditorDocument,
} from './editorStore';
import { Viewport, ZOOM_MAX, ZOOM_MIN, type ViewportHandle } from './Viewport';
import { ApiProblem } from '@cowoo/shared/api';

/** Until a scale is set the plan is a blank grid of this size (§ 6.2). */
const DEFAULT_BOUNDS = { widthCm: 2000, heightCm: 1400 };

/** § 6.3 — 25 % to 400 %, in the steps the + and − tiles take. */
const ZOOM_STEPS = [ZOOM_MIN, 0.5, 0.75, 1, 1.5, 2, 3, ZOOM_MAX];

export function Editor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId = '' } = useParams();

  const store = useEditorStore();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const labelField = useRef<HTMLInputElement>(null);
  const [tool, setTool] = useState<Tool>('select');
  const [query, setQuery] = useState('');
  const [occupancy, setOccupancy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<ViewportHandle>(null);

  /**
   * Which plan this is, and which building it stands in.
   *
   * The location came from a `?locationId=` search parameter that nothing put
   * there — every route into the editor omitted it — so the autosave PUT sent
   * an empty string where the server wants a uuid and failed on every keystroke
   * behind a retry with backoff. It is read from the plan itself now, which is
   * also what the header needs to say more than a raw id.
   */
  const plan = useQuery({
    queryKey: ['admin', 'floor-plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plans');
      if (error) throw error;
      return data.items;
    },
    select: (items) => items.find((item) => item.id === planId) ?? null,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  const locationId = plan.data?.locationId ?? '';
  const locationName = locations.data?.find((item) => item.id === locationId)?.name ?? '';

  /**
   * The draft, **with what is on it**.
   *
   * `POST …/draft` answers with the version's id and revision and nothing
   * else, so loading an empty document from it opened every plan as a blank
   * sheet — and since autosave sends the whole document, the first object
   * placed would have deleted every desk that was already there. The contents
   * come from the preview endpoint, which is the same data the member's picker
   * reads.
   */
  const draft = useQuery({
    queryKey: ['admin', 'plan-draft', planId],
    enabled: planId !== '',
    queryFn: async () => {
      const created = await api.POST('/admin/floor-plans/{id}/draft', {
        params: { path: { id: planId } },
      });
      if (created.error) throw created.error;

      const contents = await api.GET('/admin/floor-plan-versions/{vid}/preview', {
        params: { path: { vid: created.data.id } },
      });
      if (contents.error) throw contents.error;
      return { ...created.data, plan: contents.data };
    },
  });

  useEffect(() => {
    if (!draft.data) return;
    // The same draft again, only newer (the upload screen saved a scale, a
    // refetch caught up): adopt it as long as nothing here is unsaved. A
    // conflict is not adopted behind the dialog's back — its "Neu laden" is
    // the one place that drops what was typed.
    const newer = store.versionId === draft.data.id && draft.data.rev > store.rev;
    if (store.versionId === draft.data.id && !(newer && store.save.status === 'clean')) return;
    store.load(draft.data.id, draft.data.rev, documentFrom(draft.data.plan));
  }, [draft.data, store]);

  /**
   * § 6.6 rule 3 — "Neu laden": the server's draft replaces this one, typed
   * changes included. A refetch alone did not do that: the store kept its
   * document and its old `rev`, the next edit cleared the conflict, the next
   * autosave hit the same 409, and the dialog came back for good.
   */
  const reloadFromServer = useCallback(async () => {
    const { data } = await draft.refetch();
    if (data) useEditorStore.getState().load(data.id, data.rev, documentFrom(data.plan));
  }, [draft]);

  /** § 6.6 — the full document under the last known `rev`. */
  const save = useCallback(async (): Promise<void> => {
    const { versionId, rev, document, save: state } = useEditorStore.getState();
    if (versionId === null || state.status === 'conflict' || locationId === '') return;

    useEditorStore.getState().markSaving();
    try {
      const { data } = await api.PUT('/admin/floor-plan-versions/{vid}', {
        params: { path: { vid: versionId } },
        body: { rev, locationId, ...document },
      });
      if (!data) return void useEditorStore.getState().markFailed();
      useEditorStore.getState().markSaved(data.rev, Date.now());
    } catch (error) {
      // The client throws on every non-ok answer (04 § 8.1); a 409 is the one
      // that is not a failure but a decision (§ 6.6 rule 3): block and let the
      // user choose. Reading `response.status` here never saw it.
      const problem = ApiProblem.fromThrown(error);
      if (problem.status === 409) useEditorStore.getState().markConflict(problem.ext.rev ?? rev + 1);
      else useEditorStore.getState().markFailed();
    }
  }, [locationId]);

  // Autosave 1.5 s after the last change; a failure retries with backoff while
  // editing continues locally.
  useEffect(() => {
    if (store.save.status !== 'dirty' && store.save.status !== 'failed') return;
    const delay = store.save.status === 'failed' ? backoffFor(store.save.attempt) : AUTOSAVE_DEBOUNCE_MS;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void save(), delay);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [store.save, save]);

  // § 6.6 rule 4 — and a blur save, so switching tabs does not lose work.
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent): void => {
      if (!shouldWarnOnUnload(useEditorStore.getState().save)) return;
      event.preventDefault();
    };
    const flush = (): void => {
      if (useEditorStore.getState().save.status === 'dirty') void save();
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('blur', flush);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('blur', flush);
    };
  }, [save]);

  const mutate = useCallback((change: (objects: EditorObject[]) => EditorObject[]) => {
    useEditorStore.getState().apply((document) => ({
      ...document,
      placements: change(document.placements),
    }));
  }, []);

  /**
   * § 6.2 — with a picture the floor *is* the picture: the canvas is as large
   * as the plan at its measured scale, so nothing is cut off at the sheet's
   * default 20 × 14 m and a small plan does not sit in the corner of one.
   */
  const backgroundSize = backgroundSizeCm(draft.data?.plan);
  const widthCm = backgroundSize?.widthCm;
  const heightCm = backgroundSize?.heightCm;
  const bounds = useMemo(
    () => (widthCm && heightCm ? { widthCm, heightCm } : DEFAULT_BOUNDS),
    [widthCm, heightCm],
  );

  const zoomBy = useCallback((direction: 1 | -1) => {
    setZoom((current) => {
      const index = ZOOM_STEPS.findIndex((step) => step >= current - 0.001);
      const next = ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, index + direction))];
      return next ?? current;
    });
  }, []);

  // § 6.3 — the shortcuts, on the document so they work wherever focus sits,
  // except inside a field where the same keys are text.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      const state = useEditorStore.getState();
      const selected = state.document.placements.filter((object) =>
        state.selection.includes(object.resourceId),
      );
      const meta = event.metaKey || event.ctrlKey;

      if (meta && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        return event.shiftKey ? state.redo() : state.undo();
      }
      if (meta && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        return state.select(
          state.document.placements.filter((o) => o.kind === 'desk').map((o) => o.resourceId),
        );
      }
      if (meta && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        return mutate((objects) => [
          ...objects,
          ...selected.map((object) =>
            duplicateObject(object, objects, state.document.gridCm, bounds, crypto.randomUUID()),
          ),
        ]);
      }
      if (event.key === 'Escape') {
        setTool('select');
        return state.select([]);
      }
      // § 6.3 — zoom: + / − / 0 for fit.
      if (event.key === '+' || event.key === '=') return zoomBy(1);
      if (event.key === '-') return zoomBy(-1);
      if (event.key === '0') return viewportRef.current?.fit();
      // § 6.3 — Enter on a selection focuses "Nummer".
      if (event.key === 'Enter' && selected.length > 0) {
        event.preventDefault();
        return labelField.current?.focus();
      }

      // The rail's shortcuts. `R` is resolved by whether anything is selected,
      // which is why this runs before the rotate branch below.
      const chosen = toolForKey(event.key, selected.length > 0);
      if (chosen && chosen !== 'rotate') {
        event.preventDefault();
        return setTool(chosen);
      }
      if (selected.length === 0) return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        return mutate((objects) => objects.filter((o) => !state.selection.includes(o.resourceId)));
      }
      if (event.key.toLowerCase() === 'r') {
        // Shift+R turns by 15°, plain R by a quarter turn (§ 6.3).
        const degrees = event.shiftKey ? 15 : 90;
        return mutate((objects) =>
          objects.map((o) => (state.selection.includes(o.resourceId) ? rotateObject(o, degrees) : o)),
        );
      }

      const nudge = ARROWS[event.key];
      if (nudge) {
        event.preventDefault();
        // Alt nudges by a single centimetre, without snapping.
        const step = event.altKey ? 1 : state.document.gridCm * (event.shiftKey ? 10 : 1);
        return mutate((objects) =>
          objects.map((o) =>
            state.selection.includes(o.resourceId)
              ? moveObject({
                  object: o,
                  deltaXCm: nudge.x * step,
                  deltaYCm: nudge.y * step,
                  gridCm: state.document.gridCm,
                  snap: !event.altKey,
                  bounds,
                })
              : o,
          ),
        );
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mutate, zoomBy, bounds]);

  /**
   * § 6.3 — a placed object takes the next free number and the zone of the
   * last one placed, which is almost always the zone the user is working in.
   */
  const place = useCallback(
    (kind: EditorObject['kind'], xCm: number, yCm: number, size?: { wCm: number; hCm: number }): void =>
      mutate((objects) => [
        ...objects,
        clampToBounds(
          {
            resourceId: crypto.randomUUID(),
            kind,
            // Desks are numbered; the other kinds are named after themselves —
            // "Büro 2", "Raum 1" — as the plan's own labels are.
            label: nextFreeLabel(
              objects.filter((object) => object.kind === kind),
              kind === 'desk' ? '' : `${t(`admin.editor.tool.${kind}`)} `,
            ),
            zone: [...objects].reverse().find((object) => object.kind === kind)?.zone ?? null,
            xCm: snapToGrid(xCm, useEditorStore.getState().document.gridCm, useEditorStore.getState().snap),
            yCm: snapToGrid(yCm, useEditorStore.getState().document.gridCm, useEditorStore.getState().snap),
            // A drawn rectangle keeps its size, on the grid and within the kind's range.
            ...(size
              ? {
                  wCm: clampSize(
                    snapToGrid(
                      size.wCm,
                      useEditorStore.getState().document.gridCm,
                      useEditorStore.getState().snap,
                    ),
                    kind,
                  ),
                  hCm: clampSize(
                    snapToGrid(
                      size.hCm,
                      useEditorStore.getState().document.gridCm,
                      useEditorStore.getState().snap,
                    ),
                    kind,
                  ),
                }
              : DEFAULT_SIZE_CM[kind]),
            rotationDeg: 0,
            deskType: kind === 'desk' ? 'flex' : null,
            status: 'bookable',
            ...DEFAULT_RULES,
          },
          bounds,
        ),
      ]),
    [mutate, bounds, t],
  );

  /**
   * § 6.3's "Belegung heute" — today's live states drawn over the draft.
   *
   * Display only: the overlay never touches the document, because what is
   * booked today has nothing to do with what the next version of the plan
   * looks like. It is here so that "can we take this row out on Thursday" can
   * be answered while looking at the row.
   */
  const live = useQuery({
    queryKey: ['admin', 'occupancy', locationId],
    enabled: occupancy && locationId !== '',
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/occupancy', {
        params: { path: { id: locationId } },
      });
      if (error) throw error;
      return new Map(data.desks.map((desk) => [desk.resourceId, desk.state] as const));
    },
  });

  const selected = store.document.placements.find((object) => store.selection.includes(object.resourceId));
  const enriched = draft.data?.plan.placements.find((p) => p.resourceId === selected?.resourceId);
  const title = t('admin.plans.cardName', { location: locationName, floor: plan.data?.name ?? '' });

  return (
    // The shell's header is 64 px; the editor takes exactly the rest, so the
    // rail's zoom tiles and the canvas footer stay on screen instead of below
    // a page scroll — the board is one screen, and so is this.
    <div className="bg-fog flex h-[calc(100vh-64px)] flex-col">
      <EditorBar
        onBack={() => void navigate('/grundrisse')}
        title={title}
        chip={<DraftChip>{t('admin.editor.draft')}</DraftChip>}
        subline={
          // § 6.3's line says "gespeichert {{time}}", so only the time goes
          // in; "Speichert …", "Nicht gespeichert" and "noch nicht
          // gespeichert" replace that clause rather than filling it.
          store.save.status === 'clean' && store.save.savedAt !== null
            ? t('admin.editor.header', {
                version: draft.data?.version ?? 0,
                time: formatTime(new Date(store.save.savedAt).toISOString()),
                previous: plan.data?.published?.version ?? 0,
              })
            : `${t('admin.editor.headerShort', { version: draft.data?.version ?? 0 })} · ${saveLabel(store.save, t)}`
        }
        actions={
          <>
            {/* The board's toggle: a bordered box holding the label and the switch. */}
            <label
              className={`border-line text-adminBody text-ink rounded-input flex h-10 cursor-pointer items-center gap-2.5 border pl-3.5 pr-3 font-semibold ${
                occupancy ? 'bg-teal-tint' : 'bg-white'
              }`}
            >
              <span>{t('admin.editor.occupancyToggle')}</span>
              <Switch checked={occupancy} onChange={setOccupancy} label={t('admin.editor.occupancyToggle')} />
            </label>
            <Button
              size="md"
              variant="secondary"
              onClick={() =>
                // § 8's "Auswahl auf dem Plan" is only offered where a
                // selection can be carried, so the editor carries it.
                void navigate(`/grundrisse/${planId}/aufkleber`, { state: { selection: store.selection } })
              }
            >
              <Icon name="sticker" size={16} />
              {t('admin.plans.stickers')}
            </Button>
            <Button
              size="md"
              variant="secondary"
              onClick={() => void navigate(publishHref(planId, store.versionId, locationId))}
            >
              {t('admin.editor.preview')}
            </Button>
            <Button size="md" onClick={() => void navigate(publishHref(planId, store.versionId, locationId))}>
              {t('admin.editor.publish')}
            </Button>
          </>
        }
      />

      {store.save.status === 'conflict' ? (
        <ConflictDialog
          onReload={() => void reloadFromServer()}
          onKeepMine={() => useEditorStore.getState().keepMine()}
          onExport={() => downloadJson(useEditorStore.getState().exportDocument(), planId)}
        />
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-[56px_232px_minmax(0,1fr)_320px]">
        <ToolRail
          active={tool}
          onSelect={setTool}
          zoom={zoom}
          onZoom={zoomBy}
          onRelabel={() => labelField.current?.focus()}
        />

        <aside className="border-line flex min-h-0 flex-col border-r bg-white">
          <ObjectList
            objects={store.document.placements}
            selection={store.selection}
            query={query}
            onQuery={setQuery}
            onSelect={store.select}
          />
        </aside>

        <div className="relative min-h-0 min-w-0 overflow-hidden">
          {/* § 6.3 — the legend, as chips on the canvas. */}
          <div className="absolute left-6 top-4 z-[2] flex gap-2">
            <LegendChip>{t('admin.editor.legendBookable')}</LegendChip>
            <LegendChip>{t('admin.editor.legendBlocked')}</LegendChip>
            <LegendChip>{t('admin.editor.legendMaintenance')}</LegendChip>
          </div>
          <Viewport
            ref={viewportRef}
            zoom={zoom}
            onZoom={setZoom}
            className="h-full w-full overflow-auto px-6 pb-14 pt-16"
          >
            <Canvas
              objects={store.document.placements}
              selection={store.selection}
              bounds={bounds}
              gridCm={store.document.gridCm}
              snap={store.snap}
              zoom={zoom}
              pxPerCm={store.document.pxPerCm}
              // § 6.2 — the plan is what the objects are placed *on*; the
              // canvas has always been able to draw it and was never given it.
              backgroundUrl={draft.data?.plan.imageUrl}
              backgroundOpacity={store.document.backgroundOpacity}
              backgroundSizeCm={backgroundSize}
              liveStateOf={occupancy ? (id) => live.data?.get(id) ?? 'free' : undefined}
              onSelect={store.select}
              tool={tool}
              onPlace={(xCm, yCm, size) => {
                if (tool === 'select') return;
                place(tool, xCm, yCm, size);
                // § 6.3 — the placing tools stay active for serial placement.
              }}
              onGesture={(phase) =>
                phase === 'start'
                  ? useEditorStore.getState().beginGesture()
                  : useEditorStore.getState().endGesture()
              }
              onMove={(moved) =>
                mutate((objects) =>
                  objects.map((object) => moved.find((m) => m.resourceId === object.resourceId) ?? object),
                )
              }
            />
          </Viewport>
          <div className="text-muted absolute bottom-4 left-6 flex items-center gap-2 text-xs">
            <button type="button" onClick={() => store.setSnap(!store.snap)} className="contents">
              <LegendChip small>
                {t('admin.editor.grid', { grid: store.document.gridCm })} ·{' '}
                {store.snap ? t('admin.editor.snapOn') : t('admin.editor.snapOff')}
              </LegendChip>
            </button>
            <span className="font-body">
              {tool === 'select' ? t('admin.editor.shortcuts') : t(`admin.editor.toolHint.${tool}`)}
            </span>
          </div>
        </div>

        <aside className="border-line flex min-h-0 flex-col overflow-hidden border-l bg-white">
          {selected ? (
            <ObjectPanel
              object={selected}
              all={store.document.placements}
              assignedTo={enriched?.assignedTo ?? null}
              bookingsToday={enriched?.bookingsToday ?? 0}
              bookingsUpcoming={enriched?.bookingsUpcoming ?? 0}
              labelRef={labelField}
              onChange={(next) =>
                mutate((objects) =>
                  objects.map((object) => (object.resourceId === next.resourceId ? next : object)),
                )
              }
              onRemove={() =>
                mutate((objects) => objects.filter((o) => o.resourceId !== selected.resourceId))
              }
            />
          ) : (
            <EmptyPanel />
          )}
        </aside>
      </div>
    </div>
  );
}

/** A white bordered chip on the canvas, for the legend and the grid line. */
function LegendChip({ children, small }: { children: React.ReactNode; small?: boolean }) {
  return (
    <span
      className={`border-line text-ink font-body inline-flex items-center rounded-full border bg-white px-2.5 text-[13px] font-semibold ${
        small ? 'h-[26px]' : 'h-[30px]'
      }`}
    >
      {children}
    </span>
  );
}

/**
 * Nothing selected: the shortcuts, centred, with the keys drawn as keys.
 *
 * The board does not draw this state at all — it always has a desk selected —
 * so it follows the owner's instruction for it: centred both ways, and every
 * key combination as `<kbd>`, so "Cmd+D" reads as two keys rather than a word.
 */
function EmptyPanel() {
  const { t } = useTranslation();
  const rows: Array<{ keys: string[]; label: string }> = [
    { keys: ['V'], label: t('admin.editor.tool.select') },
    { keys: ['D'], label: t('admin.editor.tool.desk') },
    { keys: ['R'], label: t('admin.editor.keyRotate') },
    { keys: ['Entf'], label: t('admin.editor.keyDelete') },
    { keys: ['⌘', 'D'], label: t('admin.editor.keyDuplicate') },
    { keys: ['⌘', 'Z'], label: t('admin.editor.keyUndo') },
    { keys: ['Esc'], label: t('admin.editor.keyEscape') },
  ];
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
      <Icon name="select" size={28} className="text-placeholder" />
      <p className="text-adminBody text-muted">{t('admin.editor.emptyPanel')}</p>
      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-left">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="flex justify-end gap-1">
              {row.keys.map((key) => (
                <Kbd key={key}>{key}</Kbd>
              ))}
            </dt>
            <dd className="text-adminSmall text-muted">{row.label}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The editor's document from what `/preview` serves — the same mapping on load and on reload. */
function documentFrom(contents: {
  pxPerCm: number;
  gridCm: number;
  backgroundOpacity: number;
  placements: Array<Omit<EditorObject, 'kind' | 'maxAdvanceDays'> & { kind: string; maxAdvanceDays: number }>;
}): EditorDocument {
  return {
    pxPerCm: contents.pxPerCm,
    gridCm: contents.gridCm,
    backgroundOpacity: contents.backgroundOpacity,
    placements: contents.placements.map((placement) => ({
      resourceId: placement.resourceId,
      kind: placement.kind as EditorObject['kind'],
      label: placement.label,
      zone: placement.zone,
      xCm: placement.xCm,
      yCm: placement.yCm,
      wCm: placement.wCm,
      hCm: placement.hCm,
      rotationDeg: placement.rotationDeg,
      deskType: placement.deskType,
      status: placement.status,
      attributes: placement.attributes,
      capacity: placement.capacity,
      bookableBy: placement.bookableBy,
      maxAdvanceDays: placement.maxAdvanceDays as EditorObject['maxAdvanceDays'],
    })),
  };
}

/**
 * The plan image at the scale it was measured at (§ 6.2).
 *
 * `null` while the plan has no picture or no usable scale, which is the case
 * the canvas falls back to its own bounds for.
 */
function backgroundSizeCm(plan?: {
  imageWidthPx: number | null;
  imageHeightPx: number | null;
  pxPerCm: number;
}): { widthCm: number; heightCm: number } | null {
  if (!plan?.imageWidthPx || !plan.imageHeightPx || plan.pxPerCm <= 0) return null;
  return {
    widthCm: plan.imageWidthPx / plan.pxPerCm,
    heightCm: plan.imageHeightPx / plan.pxPerCm,
  };
}

/** § 7 — the preview and the publish screen are the same route. */
function publishHref(planId: string, versionId: string | null, locationId: string): string {
  return `/grundrisse/${planId}/veroeffentlichen?version=${versionId ?? ''}&locationId=${locationId}`;
}

const ARROWS: Record<string, { x: number; y: number } | undefined> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

function saveLabel(
  save: ReturnType<typeof useEditorStore.getState>['save'],
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  switch (save.status) {
    case 'saving':
      return t('admin.editor.saving');
    case 'failed':
      return t('admin.editor.failed');
    case 'clean':
      return save.savedAt === null
        ? t('admin.editor.never')
        : t('admin.editor.saved', { time: formatTime(new Date(save.savedAt).toISOString()) });
    case 'conflict':
      return t('admin.editor.conflictTitle');
    default:
      return t('admin.editor.never');
  }
}

/**
 * § 6.6 rule 3 — blocking. Three ways out, none of them a merge: keep this
 * version (D-114: the other writer's save is overwritten), reload theirs
 * (what was typed here is lost), or export what was typed first.
 */
function ConflictDialog({
  onReload,
  onKeepMine,
  onExport,
}: {
  onReload: () => void;
  onKeepMine: () => void;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-ink/40 absolute inset-0 z-10 flex items-center justify-center p-6">
      <Card className="max-w-md">
        <h2 className="font-heading text-ink text-lg">{t('admin.editor.conflictTitle')}</h2>
        <p className="font-body text-text mt-2 text-sm">{t('admin.editor.conflictBody')}</p>
        <div className="mt-4 flex flex-col gap-2">
          <Button onClick={onKeepMine} data-testid="conflict-keep-mine">
            {t('admin.editor.conflictKeepMine')}
          </Button>
          <Button variant="secondary" onClick={onExport}>
            {t('admin.editor.conflictExport')}
          </Button>
          <Button variant="danger" onClick={onReload} data-testid="conflict-reload">
            {t('admin.editor.conflictReload')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function downloadJson(json: string, name: string): void {
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${name}-entwurf.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
